import React from 'react'
import { CSVLink } from "react-csv";
import {
    DataTable,
    Table,
    Link,
    Button,
    Pagination,
    TableBatchAction,
    TableBatchActions,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableHeader,
    TableRow,
    TableToolbar,
    TableToolbarAction,
    TableToolbarContent,
    TableToolbarSearch,
    TableToolbarMenu,
    Modal,
    Form,
    TextArea,
    NotificationActionButton,
    InlineNotification,
    OverflowMenu,
    OverflowMenuItem
} from 'carbon-components-react';
import {
    Delete16 as Delete,
    Edit16 as Edit,
} from '@carbon/icons-react';

const clientListHeaders = [
    { header: "ID", key: "id" },
    { header: "Action", key: "action" },
    { header: "Date", key: "date" },
    { header: "Note", key: "note" },
];

export default {
    title: 'Components/DataTable/Batch Actions',
    component: DataTable,
    subcomponents: {
        TableBatchAction,
        TableBatchActions,
        TableToolbar,
        TableToolbarAction,
        TableToolbarContent,
        TableToolbarSearch,
        TableToolbarMenu,
        Table,
        TableBody,
        TableCell,
        TableContainer,
        TableHead,
        TableHeader,
        TableRow,
    },
};

export const ClientNotesList = ({ deleteNoteID, setDeleteNoteID, deleteNote, openDeleteNoteModal, setOpenDeleteNoteModal, setOpenEditNoteModal, openEditNoteModal, editNoteForm, setEditNoteID, editNoteID, editNoteSetup, handleEditNote, handleAddNote, openAddNoteModal, setOpenAddNoteModal, addNoteForm, error, setError, rows, totalItems, currentPageSize, setCurrentPageSize, setFirstRowIndex, setNoteSearch }: any) => (
    <>
        <DataTable rows={rows} headers={clientListHeaders} isSortable>
            {({
                rows,
                headers,
                getHeaderProps,
                getRowProps,
                getSelectionProps,
                getToolbarProps,
                getBatchActionProps,
                onInputChange,
                selectedRows,
                getTableProps,
                getTableContainerProps,
            }) => {
                const batchActionProps = getBatchActionProps();
                return (
                    <TableContainer
                        {...getTableContainerProps()}>
                        <TableToolbar {...getToolbarProps()}>
                            <TableBatchActions {...batchActionProps}>
                                <TableBatchAction
                                    tabIndex={batchActionProps.shouldShowBatchActions ? 0 : -1}
                                    renderIcon={Delete}

                                >
                                    Delete
                                </TableBatchAction>
                            </TableBatchActions>
                            <TableToolbarContent
                                aria-hidden={batchActionProps.shouldShowBatchActions}>
                                <TableToolbarSearch
                                    persistent={true}
                                    tabIndex={batchActionProps.shouldShowBatchActions ? -1 : 0}
                                    placeholder="Search clients"
                                    onChange={(e) => setNoteSearch(e.target.value)}
                                />
                                <TableToolbarMenu
                                    tabIndex={batchActionProps.shouldShowBatchActions ? -1 : 0}>
                                    <TableToolbarAction>
                                        Export to CSV
                                    </TableToolbarAction>
                                </TableToolbarMenu>
                                <Button
                                    tabIndex={batchActionProps.shouldShowBatchActions ? -1 : 0}
                                    size="small"
                                    kind="primary"
                                    onClick={() => setOpenAddNoteModal(true)}>
                                    Add new
                                </Button>
                            </TableToolbarContent>
                        </TableToolbar>
                        <Table {...getTableProps()}>
                            <TableHead>
                                <TableRow>
                                    {headers.map((header, i) => (
                                        <TableHeader key={i} {...getHeaderProps({ header })}>
                                            {header.header}
                                        </TableHeader>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map((row, i) => (
                                    <TableRow key={i} {...getRowProps({ row })}>
                                        {row.cells.map((cell) => (
                                            <TableCell key={cell.id}>
                                                {cell.info.header === "action" ? <OverflowMenu renderIcon={Edit} className="m-auto">
                                                    <OverflowMenuItem onClick={() => editNoteSetup(row.id) & setOpenEditNoteModal(true)} itemText="Edit" />
                                                    <OverflowMenuItem onClick={() => setDeleteNoteID(row.id) & setOpenDeleteNoteModal(true)} hasDivider isDelete itemText="Delete" />
                                                </OverflowMenu> : cell.value}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        <Pagination
                            totalItems={totalItems}
                            pageSizes={[10, 20, 30, 40, 50]}
                            pageSize={currentPageSize}
                            onChange={({ page, pageSize }) => {
                                if (pageSize !== currentPageSize) {
                                    setCurrentPageSize(pageSize);
                                }
                                setFirstRowIndex(pageSize * (page - 1));
                            }}
                        />
                    </TableContainer>
                );
            }}
        </DataTable>

        <Modal
            open={openAddNoteModal}
            modalHeading="Add Client Note"
            modalLabel="Client Notes"
            primaryButtonText="Add"
            secondaryButtonText="Cancel"
            hasForm={true}
            onRequestClose={() => setOpenAddNoteModal(false) & setError(false)}
            onRequestSubmit={() => addNoteForm.current && addNoteForm.current.click() & setError(false)}
        >   {error && <InlineNotification kind="error" hideCloseButton={true} title="Error: Unable to add note" subtitle="Please try again or contact your administrator." actions={<NotificationActionButton onClick={(() => setError(false))}>Dismiss</NotificationActionButton>} />}
            <p style={{ marginBottom: '1rem' }}>
                Fill out the form to add a new client note.
            </p>
            <Form id="addNote" onSubmit={(values) => handleAddNote(values)} className="d-flex flex-w">
                <div className="col-12 m-005">
                    <TextArea
                        data-modal-primary-focus
                        id="note"
                        name="note"
                        labelText="Note"
                        placeholder="Enter note"
                        defaultValue=""
                        style={{ marginBottom: '1rem' }}
                    />
                </div>
                <input type="submit" style={{ display: 'none' }} ref={addNoteForm} />
            </Form>
        </Modal>

        <Modal
            open={openEditNoteModal}
            modalHeading={"Edit Client Note #" + editNoteID?.id}
            modalLabel="Client Notes"
            primaryButtonText="Save Changes"
            secondaryButtonText="Cancel"
            hasForm={true}
            onRequestClose={() => setOpenEditNoteModal(false) & setError(false)}
            onRequestSubmit={() => editNoteForm.current && editNoteForm.current.click() & setError(false)}
        >   {error && <InlineNotification kind="error" hideCloseButton={true} title="Error: Unable to add note" subtitle="Please try again or contact your administrator." actions={<NotificationActionButton onClick={(() => setError(false))}>Dismiss</NotificationActionButton>} />}
            <p style={{ marginBottom: '1rem' }}>
                Fill out the form to edit the selected client note (#{editNoteID?.id}). This note was added on {editNoteID?.date}.
            </p>
            <Form id="addNote" onSubmit={(values) => handleEditNote(values)} className="d-flex flex-w">
                <div className="col-12 m-005">
                    <TextArea
                        data-modal-primary-focus
                        id="note"
                        name="note"
                        labelText="Note"
                        placeholder="Enter note"
                        defaultValue={editNoteID?.note}
                        style={{ marginBottom: '1rem' }}
                    />
                </div>
                <input type="submit" style={{ display: 'none' }} ref={editNoteForm} />
            </Form>
        </Modal>

        <Modal
            danger
            open={openDeleteNoteModal}
            modalHeading={"Delete Client Note #" + deleteNoteID}
            modalLabel="Client Notes"
            primaryButtonText="Delete"
            secondaryButtonText="Cancel"
            onRequestClose={() => setOpenDeleteNoteModal(false) & setError(false)}
            onRequestSubmit={() => deleteNote(deleteNoteID) & setError(false)}
        >
            {error && <InlineNotification kind="error" hideCloseButton={true} title="Error: Unable to delete client note" subtitle="Please try again or contact your administrator." actions={<NotificationActionButton onClick={(() => setError(false))}>Dismiss</NotificationActionButton>} />}
            <p>Are you sure you want to delete the selected client note (#{deleteNoteID})?</p>

        </Modal>
    </>
);